import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { usePosOrderDetail, type PosOrderItem, type PosOrderPayment } from '@/hooks/use-pos-reports';
import { useBusinessBranding } from '@/hooks/use-business-branding';
import { useAuthStore } from '@/stores/auth-store';
import { useReportingTimeZone } from '@/hooks/use-reporting-timezone';

interface Props {
  orderId: string;
}

function fmt(value: string | number): string {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  // Sign before the currency symbol, so a Minus Amount line reads -$1.20 rather
  // than the $-1.20 that falls out of interpolating a negative. Matches how the
  // POS receipt prints its discount and deduction lines.
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `-$${abs}` : `$${abs}`;
}

function formatCardTypeLabel(cardType: string | null | undefined): string | null {
  if (!cardType) return null;
  switch (cardType) {
    case 'credit':
      return 'CREDIT';
    case 'debit':
      return 'DEBIT';
    case 'ebt_food':
      return 'EBT FOOD';
    case 'ebt_cash':
      return 'EBT CASH';
    case 'gift':
      return 'GIFT';
    case 'check':
      return 'CHECK';
    default:
      return cardType.toUpperCase();
  }
}

function formatBrandLabel(brand: string | null | undefined, fallback: string): string {
  if (!brand) return fallback.toUpperCase();
  switch (brand) {
    case 'visa':
      return 'VISA';
    case 'mastercard':
      return 'MASTERCARD';
    case 'amex':
      return 'AMEX';
    case 'discover':
      return 'DISCOVER';
    case 'diners_club':
      return 'DINERS CLUB';
    case 'jcb':
      return 'JCB';
    case 'interac':
      return 'INTERAC';
    case 'maestro':
      return 'MAESTRO';
    case 'union_pay':
      return 'UNIONPAY';
    case 'unknown':
      return fallback.toUpperCase();
    default:
      return brand.toUpperCase();
  }
}

function maskedCardNumber(lastFour: string | null | undefined): string | null {
  if (!lastFour) return null;
  return `xxxxxxxxxxxx${lastFour}`;
}

/**
 * Payment is considered EBT-funded when any payment row uses an EBT card type.
 * Drives the per-line "EBT" tag so the customer can see which items were paid
 * out of EBT funds versus regular tender.
 */
function isEbtTender(payments: PosOrderPayment[]): boolean {
  return payments.some((p) => p.cardType === 'ebt_food' || p.cardType === 'ebt_cash');
}

/** Strip characters that are illegal in file names so the download name is safe
 * across Windows/macOS/Linux. Collapses runs of whitespace to single spaces. */
function sanitizeFileName(name: string): string {
  return name
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Standalone receipt page for a single POS order. Opens in a new tab from the
 * All Orders table's Download button and, once the order loads, generates a
 * PDF of *just the receipt* and downloads it directly — no browser print
 * dialog, and none of the surrounding app chrome (header/sidebar) or browser
 * URL banner ends up in the file, because we rasterize only the receipt
 * element via its ref. The QR at the foot encodes the URL of this same page so
 * scanning it on a phone reopens the receipt preview.
 */
export function OrderReceiptPrintPage({ orderId }: Props) {
  const { data, isLoading } = usePosOrderDetail(orderId);
  // Format the receipt date in the active location's zone (the app-wide source
  // of truth) instead of a hardcoded Pacific constant.
  const { timeZone } = useReportingTimeZone();
  const order = data?.data;
  const tenantId = useAuthStore((s) => s.user?.tenantId) ?? '';
  const { data: brandingData } = useBusinessBranding(tenantId);
  const branding = brandingData?.data ?? null;

  // The receipt DOM node we rasterize into the PDF — captures this element
  // only, so app chrome and the browser URL banner are excluded.
  const receiptRef = useRef<HTMLDivElement>(null);
  const [generating, setGenerating] = useState(false);

  // QR encodes the URL of *this* page, so scanning reopens the receipt
  // preview as the same web view (effectively "this preview as image" once
  // the browser renders the page). `qrReady` flips true whether the QR
  // succeeds or fails, so auto-download never blocks on it.
  const receiptUrl = typeof window !== 'undefined' ? window.location.href : '';
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrReady, setQrReady] = useState(false);
  useEffect(() => {
    if (!receiptUrl) {
      setQrReady(true);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(receiptUrl, { margin: 1, width: 220 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      })
      .finally(() => {
        if (!cancelled) setQrReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [receiptUrl]);

  const ebtTender = useMemo(() => (order ? isEbtTender(order.payments) : false), [order]);

  // Rasterize the receipt element and save it as a single-page PDF sized to
  // the receipt itself. Libraries are dynamically imported so they stay out of
  // the main bundle and only load on this page.
  const downloadPdf = useCallback(async () => {
    const el = receiptRef.current;
    if (!el || !order) return;
    setGenerating(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas-pro'), import('jspdf')]);
      const scale = 2;
      const canvas = await html2canvas(el, { scale, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      // Page = receipt size in CSS px (canvas is 2× for crispness), so the PDF
      // is exactly the receipt with no whitespace and no pagination.
      const w = canvas.width / scale;
      const h = canvas.height / scale;
      const pdf = new jsPDF({ unit: 'px', format: [w, h], orientation: w > h ? 'landscape' : 'portrait' });
      pdf.addImage(imgData, 'PNG', 0, 0, w, h);
      const businessTitle =
        order.storeName?.trim() || order.locationName?.trim() || branding?.businessTitle?.trim() || 'Receipt';
      const orderNumber = order.dailyOrderNumber ?? order.id;
      pdf.save(`${sanitizeFileName(`${businessTitle} Order ${orderNumber}`)}.pdf`);
    } finally {
      setGenerating(false);
    }
  }, [order, branding]);

  // Auto-download only when opened with `?download=1` (the All Orders table and
  // the statement's per-order "Download PDF" action pass it). Without the flag
  // — e.g. the statement's "View details" — the page just shows the receipt so
  // the user can read it and download on demand via the button. Guarded so a
  // remount doesn't re-fire the download.
  const autoDownload = typeof window !== 'undefined' && /[?&]download=1(?:&|$)/.test(window.location.hash);
  const hasDownloadedRef = useRef(false);
  useEffect(() => {
    if (autoDownload && order && qrReady && !hasDownloadedRef.current) {
      hasDownloadedRef.current = true;
      void downloadPdf();
    }
  }, [autoDownload, order, qrReady, downloadPdf]);

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }
  if (!order) {
    return <div className="flex h-screen items-center justify-center">Order not found.</div>;
  }

  // Store header values come from POS Config → Store Config (per-location
  // row in `pos_tenant_config`): storeName / storePhone / storeAddress /
  // city / state. Fall back to the public locations record and finally to
  // tenant branding if a location hasn't been configured yet. Never
  // hard-coded.
  const businessTitle =
    order.storeName?.trim() || order.locationName?.trim() || branding?.businessTitle?.trim() || 'Receipt';
  const orderNumber = order.dailyOrderNumber ?? order.id;
  // Register / station the order was rung up on. `stationName` is the
  // friendly device name from pos_devices (same source as the All Orders
  // REGISTER column and the order-details dialog); fall back to the short
  // register id the way that column does, so the receipt never shows a bare
  // UUID, and to a dash when the order carries no register at all.
  const registerLabel = order.stationName?.trim() || (order.registerId ? `Reg ${order.registerId.slice(0, 6)}` : '—');
  const orderDate = new Date(order.createdAt).toLocaleString('en-US', {
    timeZone,
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const subtotal = parseFloat(order.subtotal);
  const tax = parseFloat(order.tax);
  const total = parseFloat(order.total);
  // Merchant-configured service fees — shown under the label set in Store
  // Config, matching the printed POS receipt. Only rendered when non-zero.
  const customFee1Total = parseFloat(order.customFee1Total ?? '0');
  const customFee2Total = parseFloat(order.customFee2Total ?? '0');
  const customFee1Label = order.customFee1Label?.trim() || 'Custom Fee 1';
  const customFee2Label = order.customFee2Label?.trim() || 'Custom Fee 2';

  // Address line: <street>  Ph- <phone>. Street is whatever the cashier
  // entered under Store Config (free-form text in pos_tenant_config.store_
  // address), with a fallback to the public locations.address column for
  // legacy rows that pre-date POS Config.
  const streetLine = (order.storeAddress ?? order.locationAddress ?? '').trim();
  const phoneOnly = order.storePhone?.trim() ?? '';
  const headerAddress = [streetLine, phoneOnly ? `Ph- ${phoneOnly}` : ''].filter(Boolean).join(' ');
  // City + state on a second line, exactly as cashiers entered them on the
  // POS Config form.
  const cityStateLine = [order.storeCity, order.storeState]
    .map((s) => s?.trim() ?? '')
    .filter(Boolean)
    .join(', ');
  // Customer-facing footer is editable per location under POS Config; fall
  // back to a sensible default if blank.
  const receiptFooter =
    order.receiptFooter?.trim() ||
    `Thank You for shopping @ ${businessTitle}\nAll Exchanges and Return must be accompanied with a Receipt within 10 days. No returns/exchanges on Frozen, Fresh Produce, or Utensils.`;

  return (
    <>
      <div
        ref={receiptRef}
        className="mx-auto max-w-md bg-white px-6 py-6 text-[13px] leading-snug text-black print:max-w-none print:px-2 print:py-2"
      >
        {/* Store header */}
        <div className="mb-2 text-center">
          <h1 className="text-lg font-bold">{businessTitle}</h1>
          {headerAddress && <p className="text-[12px]">{headerAddress}</p>}
          {cityStateLine && <p className="text-[12px]">{cityStateLine}</p>}
        </div>

        {/* Order meta */}
        <div className="mb-2 space-y-0.5 text-[12px]">
          <MetaRow label="Order Number" value={String(orderNumber)} />
          <MetaRow label="Order Name" value={order.note?.trim() || 'N/A'} />
          <MetaRow label="Date" value={orderDate} />
          <MetaRow label="Employee Name" value={order.employeeName || '—'} />
          <MetaRow label="Register" value={registerLabel} />
        </div>

        <Divider />

        {/* Items table */}
        <div className="mb-1 grid grid-cols-[2rem_1fr_5rem] gap-x-2 text-[12px] font-semibold">
          <div>Qty</div>
          <div>Product</div>
          <div className="text-right">Total</div>
        </div>
        <Divider thin />

        <div className="space-y-2">
          {order.items.length === 0 ? (
            <div className="py-3 text-center text-[12px] text-gray-500">No items on this order.</div>
          ) : (
            order.items.map((item) => <ItemRow key={item.id} item={item} ebtTender={ebtTender} />)
          )}
        </div>

        <Divider />

        {/* Totals */}
        <div className="space-y-0.5 text-[12px]">
          <TotalsRow label="Total" value={fmt(subtotal)} />
          <TotalsRow label="Tax" value={fmt(tax)} />
          {customFee1Total > 0.005 && <TotalsRow label={customFee1Label} value={fmt(customFee1Total)} />}
          {customFee2Total > 0.005 && <TotalsRow label={customFee2Label} value={fmt(customFee2Total)} />}
          <TotalsRow label="Final Total" value={fmt(total)} bold />
        </div>

        <Divider />

        {/* Payment Details */}
        <div className="mb-1 text-center text-[12px] font-bold tracking-wider">PAYMENT DETAILS</div>
        <Divider thin />
        <div className="space-y-0.5 text-[12px]">
          {order.payments.length === 0 ? (
            <div className="py-1 text-gray-500">No payments captured.</div>
          ) : (
            order.payments.map((p) => <PaymentDetails key={p.id} payment={p} />)
          )}
        </div>

        <Divider />

        {/* Reward Card */}
        <div className="mb-2 text-[12px]">
          <MetaRow label="Reward Card" value="N/A" />
        </div>

        <Divider />

        {/* Customer-facing footer — comes from POS Config (receiptFooter); the
          fallback default lives in receiptFooter above. Newlines split into
          paragraphs so cashiers can format multi-line policy text. */}
        <div className="mt-2 whitespace-pre-line text-center text-[12px] leading-tight">{receiptFooter}</div>

        {/* QR code — encodes this page's URL so scanning opens the preview */}
        <div className="mt-4 flex flex-col items-center gap-1 text-[11px]">
          <div className="font-semibold">Order Barcode</div>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="Receipt QR code" className="h-32 w-32 object-contain" />
          ) : (
            <div className="h-32 w-32 bg-gray-100" />
          )}
          <div className="tracking-wider">{orderNumber}</div>
        </div>
      </div>
      <ReceiptControls generating={generating} onDownload={downloadPdf} />
    </>
  );
}

/**
 * Renders the receipt and, alongside it (outside the captured element), the
 * download controls. Wraps the exported page component so the action buttons
 * are never rasterized into the PDF.
 */
function ReceiptControls({ generating, onDownload }: { generating: boolean; onDownload: () => void }) {
  return (
    <div className="mx-auto mb-6 mt-2 flex max-w-md justify-center gap-3">
      <button
        onClick={onDownload}
        disabled={generating}
        className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
      >
        {generating ? 'Generating…' : 'Download PDF'}
      </button>
      <button
        onClick={() => window.close()}
        className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
      >
        Close
      </button>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex">
      <span className="w-32 shrink-0">{label}:</span>
      <span className="flex-1">{value}</span>
    </div>
  );
}

function TotalsRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-bold' : ''}`}>
      <span>{label}:</span>
      <span>{value}</span>
    </div>
  );
}

function Divider({ thin }: { thin?: boolean }) {
  return <div className={`my-2 border-t border-dashed border-gray-400 ${thin ? 'opacity-60' : ''}`} />;
}

function ItemRow({ item, ebtTender }: { item: PosOrderItem; ebtTender: boolean }) {
  const qtyDisplay =
    item.weightKg != null ? item.weightKg.toFixed(3) : item.qty % 1 === 0 ? String(item.qty) : item.qty.toFixed(2);
  const showUnitLine = item.qty > 1 || item.weightKg != null;
  const unitLine =
    item.weightKg != null
      ? `${item.weightKg.toFixed(3)} lb @ ${fmt(item.unitPrice)}/lb`
      : `@ ${fmt(item.unitPrice)} each`;
  // EBT tag fires when the order is paid with EBT *and* the product is
  // EBT-eligible — otherwise the marker would be misleading on items not
  // covered by the EBT tender.
  const showEbtTag = ebtTender && item.ebtEligible;

  return (
    <div className="grid grid-cols-[2rem_1fr_5rem] gap-x-2 text-[12px]">
      <div>{qtyDisplay}</div>
      <div>
        <div className="flex items-start gap-2">
          <span className="break-words font-medium">{item.productName}</span>
          {showEbtTag && (
            <span className="shrink-0 rounded border border-black px-1 text-[10px] font-bold leading-tight">EBT</span>
          )}
        </div>
        {showUnitLine && <div className="text-[11px] text-gray-700">{unitLine}</div>}
        {item.discount > 0 && (
          <div className="flex justify-between text-[11px] italic">
            {/* Promotion prints under its name; a plain line discount (manual
                or migrated) prints as "Discount (10%)" so it isn't mislabelled
                as a promotion. */}
            <span>
              {item.appliedPromotionName
                ? `Promo: ${item.appliedPromotionName}`
                : item.discountPercent
                  ? `Discount (${item.discountPercent}%)`
                  : 'Discount'}
            </span>
            <span>-{fmt(item.discount)}</span>
          </div>
        )}
      </div>
      <div className="text-right">{fmt(item.lineTotal)}</div>
    </div>
  );
}

function PaymentDetails({ payment }: { payment: PosOrderPayment }) {
  const brandLabel = formatBrandLabel(payment.cardBrand, payment.method);
  const card = maskedCardNumber(payment.cardLastFour);
  const typeLabel = formatCardTypeLabel(payment.cardType);
  return (
    <div className="space-y-0.5">
      <div className="flex justify-between">
        <span>{brandLabel}:</span>
        <span>{fmt(payment.amount)}</span>
      </div>
      {typeLabel && <MetaRow label="Type" value={typeLabel} />}
      {payment.authCode && <MetaRow label="Auth Code" value={payment.authCode} />}
      {card && <MetaRow label="Card Number" value={card} />}
      {payment.responseCode && <MetaRow label="Resp" value={payment.responseCode} />}
      {payment.terminalRef && <MetaRow label="Ref" value={payment.terminalRef} />}
    </div>
  );
}
